<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import wootConstants from 'dashboard/constants/globals';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import TeletelCopilotPanel from 'dashboard/components/copilot/TeletelCopilotPanel.vue';
import CopilotAPI from 'dashboard/api/teletel/copilot';

// Teletel's agent-side copilot. It talks only to Teletel endpoints and renders
// through Teletel's own panel, so nothing here depends on a paid flag or on
// Captain's components. The answer is produced by a background job, so this
// polls the thread briefly after each question instead of holding the request
// open.
const POLL_EVERY = 2000;
const POLL_FOR = 60000;

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();
const currentChat = useMapGetter('getSelectedChat');

const threadId = ref(null);
const messages = ref([]);
const isThinking = ref(false);
let poller = null;

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);
// Two ways in. On a conversation screen the copilot is a side-panel tab,
// opened from the rail next to the contact panel (the flag Captain's panel
// used, `is_copilot_panel_open`), rendered as a static column beside the
// conversation. Everywhere else it is the floating launcher.
const route = useRoute();
const CONVERSATION_ROUTES = [
  'inbox_conversation',
  'conversation_through_inbox',
  'conversations_through_label',
  'team_conversations_through_label',
  'conversations_through_folders',
  'conversation_through_mentions',
  'conversation_through_unattended',
  'conversation_through_participating',
  'inbox_view_conversation',
];
const onConversationScreen = computed(() =>
  CONVERSATION_ROUTES.includes(route.name)
);
const sidePanelOpen = computed(
  () =>
    onConversationScreen.value &&
    Boolean(uiSettings.value?.is_copilot_panel_open)
);
const floatingOpen = computed(
  () =>
    !onConversationScreen.value &&
    Boolean(uiSettings.value?.is_teletel_copilot_open)
);
const isOpen = computed(() => sidePanelOpen.value || floatingOpen.value);
const hasConversation = computed(() => Boolean(currentChat.value?.id));

const setOpen = value =>
  onConversationScreen.value
    ? updateUISettings({
        is_copilot_panel_open: value,
        ...(value ? { is_contact_sidebar_open: false } : {}),
      })
    : updateUISettings({ is_teletel_copilot_open: value });

// The launcher is draggable, like the visual inspector's: pointer capture,
// clamped to the viewport, remembered per browser. A press that moved more
// than a few pixels is a drag, not a click. The panel opens anchored to
// wherever the button was left.
const BUTTON = 44;
const PANEL_W = 384;
const PANEL_H = 512;
const MARGIN = 8;
const POSITION_KEY = 'teletel-copilot-launcher-position';
const position = ref(null); // { x, y } of the button's top-left, or null = default corner
let dragState = null;
let dragged = false;

const clamp = (x, y) => ({
  x: Math.min(Math.max(x, MARGIN), window.innerWidth - BUTTON - MARGIN),
  y: Math.min(Math.max(y, MARGIN), window.innerHeight - BUTTON - MARGIN),
});

const restorePosition = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(POSITION_KEY) || 'null');
    if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) {
      position.value = clamp(saved.x, saved.y);
    }
  } catch (e) {
    position.value = null;
  }
};

const persistPosition = () => {
  try {
    if (position.value)
      localStorage.setItem(POSITION_KEY, JSON.stringify(position.value));
  } catch (e) {
    // storage blocked: the position simply does not survive a reload
  }
};

const onPointerDown = event => {
  if (event.button !== 0) return;
  const rect = event.currentTarget.getBoundingClientRect();
  dragState = {
    pointerId: event.pointerId,
    offsetX: event.clientX - rect.left,
    offsetY: event.clientY - rect.top,
    startX: event.clientX,
    startY: event.clientY,
  };
  dragged = false;
  event.currentTarget.setPointerCapture?.(event.pointerId);
};

const onPointerMove = event => {
  if (!dragState || dragState.pointerId !== event.pointerId) return;
  if (
    !dragged &&
    Math.hypot(
      event.clientX - dragState.startX,
      event.clientY - dragState.startY
    ) < 4
  )
    return;
  dragged = true;
  position.value = clamp(
    event.clientX - dragState.offsetX,
    event.clientY - dragState.offsetY
  );
};

const onPointerCancel = () => {
  dragState = null;
};

const onPointerUp = event => {
  if (!dragState || dragState.pointerId !== event.pointerId) return;
  dragState = null;
  if (dragged) {
    persistPosition();
    return;
  }
  setOpen(true);
};

const launcherStyle = computed(() =>
  position.value
    ? {
        left: `${position.value.x}px`,
        top: `${position.value.y}px`,
        bottom: 'auto',
        insetInlineEnd: 'auto',
      }
    : {}
);

// Panel anchored to the button: its bottom edge on the button's bottom edge,
// spilling towards the side with more room, always inside the viewport.
const panelStyle = computed(() => {
  if (!position.value) return {};
  const { x, y } = position.value;
  const spaceRight = window.innerWidth - x;
  const left = spaceRight >= PANEL_W + MARGIN ? x : x + BUTTON - PANEL_W;
  const top = y + BUTTON - PANEL_H;
  return {
    left: `${Math.min(Math.max(left, MARGIN), window.innerWidth - PANEL_W - MARGIN)}px`,
    top: `${Math.min(Math.max(top, MARGIN), window.innerHeight - PANEL_H - MARGIN)}px`,
    bottom: 'auto',
    insetInlineEnd: 'auto',
  };
});

restorePosition();

const stopPolling = () => {
  if (poller) clearInterval(poller);
  poller = null;
  isThinking.value = false;
};

const loadMessages = async () => {
  if (!threadId.value) return;
  const { data } = await CopilotAPI.listMessages(threadId.value);
  messages.value = data.payload;
};

// The server lists only threads opened here, never Captain-era ones that
// share the table, so "the latest thread" is always one of ours.
const ensureThread = async () => {
  if (threadId.value) return threadId.value;
  const { data: list } = await CopilotAPI.listThreads();
  const thread = list.payload.length
    ? list.payload[0]
    : (await CopilotAPI.createThread()).data.payload;
  threadId.value = thread.id;
  await loadMessages();
  return threadId.value;
};

// The job answers out of band; watch the thread until the reply lands.
const awaitAnswer = () => {
  const startedAt = Date.now();
  const seen = messages.value.length;
  isThinking.value = true;
  stopPolling();
  poller = setInterval(async () => {
    await loadMessages();
    const answered = messages.value.length > seen + 1;
    if (answered || Date.now() - startedAt > POLL_FOR) stopPolling();
  }, POLL_EVERY);
};

const fail = error => {
  stopPolling();
  useAlert(error?.response?.data?.error || t('TELETEL_COPILOT.ERROR'));
};

const sendMessage = async content => {
  try {
    const id = await ensureThread();
    await CopilotAPI.ask(id, content, currentChat.value?.id ?? null);
    await loadMessages();
    awaitAnswer();
  } catch (error) {
    fail(error);
  }
};

const reset = async () => {
  stopPolling();
  try {
    const { data } = await CopilotAPI.createThread();
    threadId.value = data.payload.id;
    messages.value = [];
  } catch (error) {
    fail(error);
  }
};

// Hand the suggested reply to whichever editor the open conversation uses,
// the same bus Chatwoot's own editor already listens on.
const useReply = text => {
  const channel = currentChat.value?.meta?.channel;
  const rich = [INBOX_TYPES.WEB, INBOX_TYPES.EMAIL].includes(channel);
  emitter.emit(
    rich
      ? BUS_EVENTS.INSERT_INTO_RICH_EDITOR
      : BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR,
    text
  );
};

// immediate: the panel remembers being open across reloads, so a fresh page
// with it already open must load the thread too, not only the open transition.
watch(
  isOpen,
  async open => {
    if (open) {
      try {
        await ensureThread();
      } catch (error) {
        fail(error);
      }
    } else {
      stopPolling();
    }
  },
  { immediate: true }
);

// The AI button in the reply editor is a second way in. It opens this panel
// and, when it carries a question, asks it straight away - so the agent reads
// the answer where every other copilot answer already appears, with the same
// "insert into the editor" button, instead of a second result surface.
const askFromEditor = async question => {
  setOpen(true);
  if (question) await sendMessage(question);
};
emitter.on(BUS_EVENTS.TELETEL_COPILOT_ASK, askFromEditor);

onBeforeUnmount(() => {
  emitter.off(BUS_EVENTS.TELETEL_COPILOT_ASK, askFromEditor);
  stopPolling();
});
</script>

<template>
  <div
    v-on-click-outside="() => isSmallScreen && isOpen && setOpen(false)"
    class="teletel-copilot"
  >
    <!-- Icon only, and a fixed square: with a label inside, the launcher was a
         width-less fixed element pinned by both `ltr:right-4` and `rtl:left-4`,
         which stretched it across the whole viewport. `end-4` is the logical
         edge, so it pins to one side in either direction. -->
    <!-- Conversation screens: a side-panel tab, opened from the rail. The
         column is static in the main row on desktop (the same footprint
         Captain's panel had) and a slide-over on small screens. -->
    <div
      v-if="sidePanelOpen"
      class="fixed top-0 z-40 flex h-full w-full max-w-sm flex-col overflow-hidden border-n-weak bg-n-surface-2 shadow-lg ltr:right-0 ltr:border-l rtl:left-0 rtl:border-r md:static md:w-[320px] md:min-w-[320px] md:shadow-none 2xl:w-[360px] 2xl:min-w-[360px]"
    >
      <TeletelCopilotPanel
        :messages="messages"
        :is-thinking="isThinking"
        :has-conversation="hasConversation"
        @send="sendMessage"
        @reset="reset"
        @close="setOpen(false)"
        @use-reply="useReply"
      />
    </div>

    <!-- Everywhere else: the draggable launcher and its floating panel. -->
    <template v-else-if="!onConversationScreen">
      <button
        v-if="!floatingOpen"
        type="button"
        class="fixed bottom-4 end-4 z-50 grid size-11 cursor-grab touch-none place-content-center rounded-full bg-n-solid-2 text-n-slate-12 shadow ring-1 ring-n-weak transition-shadow hover:shadow-md active:cursor-grabbing"
        :style="launcherStyle"
        :title="t('TELETEL_COPILOT.TITLE')"
        :aria-label="t('TELETEL_COPILOT.TITLE')"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerCancel"
      >
        <span class="i-lucide-bot size-5 text-n-blue-11" />
      </button>
      <div
        v-else
        class="fixed bottom-4 end-4 z-50 flex h-[32rem] w-[24rem] max-w-[92vw] flex-col overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-xl"
        :style="panelStyle"
      >
        <TeletelCopilotPanel
          :messages="messages"
          :is-thinking="isThinking"
          :has-conversation="hasConversation"
          @send="sendMessage"
          @reset="reset"
          @close="setOpen(false)"
          @use-reply="useReply"
        />
      </div>
    </template>
  </div>
</template>
